import { formatDate } from '@brands/Utils/formatDate';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FlipIcon from '@mui/icons-material/Flip';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Button, Divider, IconButton } from '@mui/material';
import React, { Dispatch, FormEventHandler, SetStateAction, useRef } from 'react';

import { ICase } from '../../../../services/cases/types/ICase';
import { ImageNoteState } from '../../../../services/forms/types/ImageNote';
import { ImageWithNotes } from '../../../../services/forms/types/ImageWithNotes';
import { UserRoleName } from '../../../../services/identity/types/UserProfile';
import { IOneReport } from '../../../../services/oneReport/types/IOneReport';
import { updateOneReportMedia } from '../../../../services/oneReport/updateOneReportMedia';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import styles from './imageNotesModal.module.scss';

interface Modal {
  setOpenModal: Dispatch<SetStateAction<ImageNoteState>>;
  images: ImageWithNotes[];
  oneReportId: string;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  updateSelectedImages: (updatedReport: IOneReport) => void;
  media_id: string;
  readOnly: boolean;
  removeReportImage: (media_id: string, media_src: string) => void;
}

const ImageNotesModal = ({
  setOpenModal,
  images,
  oneReportId,
  thisCase,
  setCase,
  updateSelectedImages,
  media_id,
  readOnly,
  removeReportImage,
}: Modal): JSX.Element => {
  const [activeIndex, setActiveIndex] = React.useState(images.findIndex((img) => img.id === media_id));
  const [showSavePopup, setShowSavePopup] = React.useState(false);
  const activeImage = images[activeIndex];
  const [notes, setNotes] = React.useState(activeImage?.notes ?? '');

  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [flipX, setFlipX] = React.useState(false);
  const [flipY, setFlipY] = React.useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { userInfo } = useAppSelector(selectAuth);

  const hasActiveImage = Boolean(activeImage?.id && activeImage?.src);
  // DO-3961: do NOT gate Save on a non-empty note — providers need to
  // clear a previously saved note (or include an image in the OHR with no
  // note at all) and persist that change. Backend accepts note=""
  // (payload.note is Optional[str]; any non-None value writes through).
  const isSaveDisabled = readOnly || !hasActiveImage;

  const editMediaNote = async (givenNotes: string, select: boolean): Promise<void> => {
    const updatedCase = await updateOneReportMedia(
      oneReportId,
      media_id,
      {
        note: givenNotes,
        is_selected: select,
      },
      userInfo.role.name,
    );
    setCase({
      ...thisCase,
      one_report: updatedCase,
    });
    updateSelectedImages(updatedCase);
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    // DO-3961: empty notes are valid — this handler is currently dead
    // (Save uses onClick={saveNote}, not a form submit) but keeping the
    // stale guard here would contradict the PR's intent and mislead any
    // future reader wrapping the body in a <form>.
    editMediaNote(notes, true);
    setOpenModal({ isOpen: false, media_src: '', media_id: '' });
  };

  const deleteNotes = (): void => {
    if (textareaRef.current) textareaRef.current.value = '';
    setNotes('');
    editMediaNote('', false);
    updateOneReportMedia(
      oneReportId || '',
      activeImage?.id || '',
      {
        is_selected: false,
      },
      userInfo.role.name,
    );
    setOpenModal({ isOpen: false, media_src: '', media_id: '' });
  };

  const toggleSelect = async (img: ImageWithNotes) => {
    const updatedCase = await updateOneReportMedia(
      oneReportId,
      img.id!,
      { is_selected: !img.isSelected },
      userInfo.role.name,
    );

    setCase({
      ...thisCase,
      one_report: updatedCase,
    });

    updateSelectedImages(updatedCase);

    setShowSavePopup(true);
  };

  const saveNote = async () => {
    // DO-3961: empty notes are valid — allow clearing a prior note and
    // persist the (possibly empty) value. The disabled-button gate above
    // still keeps readOnly users and missing-image states out.
    if (readOnly || !activeImage?.id) return;

    const updatedCase = await updateOneReportMedia(
      oneReportId,
      activeImage.id,
      {
        note: notes.trim(),
        is_selected: true,
      },
      userInfo.role.name,
    );

    setCase({
      ...thisCase,
      one_report: updatedCase,
    });

    updateSelectedImages(updatedCase);

    setShowSavePopup(true);
  };

  const handleOpenInNewTab = () => {
    if (!activeImage?.src) return;
    // DO-3962: open the image in a new tab instead of forcing a
    // download — a forced download loses the provider's place in the
    // modal/consultation flow. This mirrors the Print button, which
    // also opens a new window and keeps the modal intact. The browser
    // renders the image inline; users can still right-click → Save
    // if they need the file on disk.
    window.open(activeImage.src, '_blank', 'noopener,noreferrer');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <body style="margin:0;display:flex;justify-content:center;align-items:center;">
          <img src="${activeImage?.src}" style="max-width:100%;" />
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  React.useEffect(() => {
    if (showSavePopup) {
      const timer = setTimeout(() => {
        setShowSavePopup(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showSavePopup]);

  React.useEffect(() => {
    setNotes(activeImage?.notes ?? '');
  }, [activeIndex]);

  React.useEffect(() => {
    setZoom(1);
    setRotation(0);
    setFlipX(false);
    setFlipY(false);
  }, [activeIndex]);

  return (
    <div className={styles.modalBackground}>
      <div className={styles.modalWrapper}>
        <div className={styles.header}>
          <div className={styles.headerDiv}>
            {activeImage?.filename}
            {activeImage?.created_at && <span>{formatDate(activeImage.created_at)}</span>}
          </div>

          <button onClick={() => setOpenModal({ isOpen: false, media_id: '', media_src: '' })}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.previewSection}>
            <img
              src={activeImage?.src}
              alt=""
              style={{
                transform: `
      scale(${zoom})
      rotate(${rotation}deg)
      scaleX(${flipX ? -1 : 1})
      scaleY(${flipY ? -1 : 1})
    `,
                transition: 'transform 0.2s ease',
              }}
            />
          </div>

          <div className={styles.notePanel}>
            <h3>Add attachment note</h3>

            <textarea
              value={notes}
              disabled={readOnly}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Type a note"
            />

            <Button variant="contained" className={styles.saveBtn} onClick={saveNote} disabled={isSaveDisabled}>
              Save attachment note
            </Button>
          </div>
        </div>

        <div className={styles.thumbnailStrip}>
          {images.map((img, index) => {
            const isActive = index === activeIndex;

            return (
              <div
                key={img.id}
                className={`${styles.thumb} ${isActive ? styles.activeThumb : styles.inactiveThumb}`}
                onClick={() => setActiveIndex(index)}
              >
                <img src={img.src} alt="" />

                <div
                  className={`${styles.thumbSelectCircle} ${img.isSelected ? styles.thumbSelected : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (readOnly) return;
                    toggleSelect(img);
                  }}
                >
                  {img.isSelected ? '✓' : ''}
                </div>

                <div
                  className={styles.removeIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (readOnly) return;
                    if (!img.id || !img.src) return;
                    removeReportImage(img.id, img.src);
                  }}
                >
                  <CloseIcon />
                </div>
              </div>
            );
          })}

          {!readOnly && userInfo.role.name === UserRoleName.Provider && (
            <label htmlFor="formId" className={styles.addThumb}>
              <AddIcon />
            </label>
          )}
        </div>

        {showSavePopup && (
          <div className={styles.savePopup}>
            <button
              onClick={() => {
                setShowSavePopup(false);
              }}
            >
              <CheckCircleIcon /> Save attachments to Oral Health Report
            </button>
          </div>
        )}
        <div className={styles.imageToolbar} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleOpenInNewTab} aria-label="Open image in new tab">
              <SaveAltIcon />
            </IconButton>
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
            <IconButton
              disabled={readOnly}
              onClick={() => {
                if (readOnly) return;
                if (!activeImage?.id || !activeImage?.src) return;
                removeReportImage(activeImage.id, activeImage.src);
              }}
            >
              <DeleteForeverIcon />
            </IconButton>
          </div>

          <Divider className={styles.toolbarDivider} orientation="vertical" flexItem sx={{ mx: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setFlipX((f) => !f)}>
              <FlipIcon />
            </IconButton>
            <IconButton onClick={() => setFlipY((f) => !f)}>
              <FlipIcon style={{ transform: 'rotate(-90deg)' }} />
            </IconButton>
            <IconButton onClick={() => setRotation((r) => r + 90)}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={() => setRotation((r) => r + 90)}>
              <Rotate90DegreesCcwIcon />
            </IconButton>
            <IconButton onClick={() => setRotation((r) => r - 90)}>
              <Rotate90DegreesCcwIcon style={{ transform: 'scaleX(-1)' }} />
            </IconButton>
          </div>

          <Divider className={styles.toolbarDivider} orientation="vertical" flexItem sx={{ mx: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}>
              <RemoveIcon />
            </IconButton>
            <IconButton onClick={() => setZoom((z) => z + 0.2)}>
              <AddIcon />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageNotesModal;
